import React from 'react';
import * as Sentry from '@sentry/react';

// When a render error escapes, React unmounts the entire tree - so one failing
// component turns into a blank page with nothing in the UI to act on. This
// boundary keeps the failure visible and recoverable, and reports it.
//
// The recurring cause here is a page translation extension (Google Translate):
// it rewraps text nodes in <font> elements, so React's later removeChild or
// insertBefore on a node it still tracks throws NotFoundError. Any element
// rendering a bare string next to another child is exposed - see the
// notranslate spans in pages/ManageCollections/CollectionTree.js.

// The fallback cannot rely on react-intl: the provider lives inside the tree
// this boundary is wrapping, and may be part of what failed.
const MESSAGES = {
    en: {
        title: 'Something went wrong',
        body:
            'Reloading usually fixes this. If you have a page translation extension enabled, ' +
            'turning it off for this site stops the problem coming back.',
        action: 'Reload',
    },
    ro: {
        title: 'A apărut o problemă',
        body:
            'De obicei reîncărcarea paginii rezolvă problema. Dacă aveți activată o extensie de traducere ' +
            'a paginii, dezactivarea ei pentru acest site împiedică reapariția problemei.',
        action: 'Reîncarcă',
    },
    fr: {
        title: 'Une erreur est survenue',
        body:
            'Recharger la page suffit généralement. Si une extension de traduction de page est activée, ' +
            'la désactiver pour ce site empêche le problème de revenir.',
        action: 'Recharger',
    },
};

function getMessages() {
    try {
        return MESSAGES[window.localStorage.getItem('language')] || MESSAGES.en;
    } catch (e) {
        return MESSAGES.en;
    }
}

const styles = {
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        padding: '2rem',
        textAlign: 'center',
        font: '400 1rem/1.5 Roboto, Helvetica, Arial, sans-serif',
        color: '#1f2937',
    },
    title: { fontSize: '1.25rem', fontWeight: 500, marginBottom: '0.75rem' },
    body: { maxWidth: '32rem', marginBottom: '1.5rem', color: '#4b5563' },
    button: {
        font: 'inherit',
        padding: '0.5rem 1.5rem',
        border: 0,
        borderRadius: '4px',
        background: '#3f51b5',
        color: '#fff',
        cursor: 'pointer',
    },
};

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error('render error', error, errorInfo);
        Sentry.captureException(error, {
            extra: { componentStack: errorInfo && errorInfo.componentStack },
        });
    }

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }

        // The fallback itself must not be translated, or the extension that
        // caused the crash can take it down too.
        let messages = getMessages();
        return (
            <div style={styles.wrapper} className="notranslate" translate="no">
                <div style={styles.title}>{messages.title}</div>
                <div style={styles.body}>{messages.body}</div>
                <button style={styles.button} onClick={() => window.location.reload()}>
                    {messages.action}
                </button>
            </div>
        );
    }
}

export default ErrorBoundary;
